import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { roomsAPI } from "../services/api";
import toast from "react-hot-toast";
import { Room, CheckInGuide } from "../types/room";
import Pagination from "../components/ui/Pagination";
import { normalizeVideoEmbedUrl } from "../utils/video";

const PAGE_SIZE = 10;

const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || "http://localhost:3000";

const EMPTY_GUIDE: CheckInGuide = {
  lockBoxCode: "", lockBoxLocation: "", lockBoxLocationEn: "", lockBoxVideoUrl: "", roomNumber: "",
  gateInstructions: "", gateInstructionsEn: "",
  buildingInstructions: "", buildingInstructionsEn: "",
  wifiName: "", wifiPassword: "",
  parkingInfo: "", parkingInfoEn: "",
  googleMapsUrl: "", addressHint: "", addressHintEn: "",
};

const DEFAULT_RULES = [
  { text: "Không hút thuốc trong phòng", allowed: false },
  { text: "Không dẫn thú cưng vào home", allowed: false },
  { text: "Không sử dụng chất kích thích", allowed: false },
  { text: "Không bật nhạc lớn sau 22:00", allowed: false },
  { text: "Không quá 2 người trong phòng", allowed: false },
  { text: "Check-out đúng giờ — cần thêm giờ báo trước nhé", allowed: true },
  { text: "Giữ gìn vệ sinh khuôn viên chung", allowed: true },
  { text: "Báo ngay nếu có hư hỏng khi check-in", allowed: true },
];

function isGuideConfigured(g?: CheckInGuide): boolean {
  if (!g) return false;
  return !!(g.lockBoxCode || g.wifiName || g.roomNumber);
}

// ─── Inline Guide Preview ─────────────────────────────────────────────────────
function GuidePreview({ room, guide }: { room: Room; guide: CheckInGuide }) {
  const [pwRevealed, setPwRevealed] = useState(false);
  const heroImg = room.images?.[0];
  const g = guide;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500&display=swap');
        .gp{--gold:#C9A84C;--gold-light:#E8D5A3;--dark:#0E0E0E;--dark-3:#1E1C1A;--text:#F0EDE8;--text-muted:#9A9590;--border:rgba(201,168,76,0.2);--serif:'Cormorant Garamond',Georgia,serif;--sans:'DM Sans',system-ui,sans-serif;background:var(--dark);color:var(--text);font-family:var(--sans);font-size:14px;line-height:1.65;-webkit-font-smoothing:antialiased;border-radius:12px;overflow:hidden}
        .gp-hdr{background:var(--dark);border-bottom:1px solid var(--border);padding:1.5rem 1.5rem 1.25rem;text-align:center;position:relative}
        .gp-hdr::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,var(--gold),transparent)}
        .gp-brand{font-size:9px;font-weight:500;letter-spacing:0.25em;color:var(--gold);text-transform:uppercase;margin-bottom:0.5rem}
        .gp-name{font-family:var(--serif);font-size:2.4rem;font-weight:600;color:var(--text);letter-spacing:0.04em;line-height:1.05}
        .gp-sub{font-family:var(--serif);font-style:italic;font-size:0.95rem;color:var(--gold-light);margin-top:0.25rem;opacity:0.8}
        .gp-div{width:50px;height:1px;background:var(--gold);margin:0.75rem auto;opacity:0.5}
        .gp-hero{width:100%;height:180px;object-fit:cover;object-position:center 40%;display:block;filter:brightness(0.88)}
        .gp-sec{padding:1.25rem 1.25rem;border-bottom:1px solid rgba(255,255,255,0.05)}
        .gp-lbl{font-size:8px;font-weight:500;letter-spacing:0.3em;text-transform:uppercase;color:var(--gold);margin-bottom:0.75rem;display:flex;align-items:center;gap:8px}
        .gp-lbl::after{content:'';flex:1;height:1px;background:var(--border)}
        .gp-addr{background:var(--dark-3);border:1px solid var(--border);border-radius:7px;padding:1rem 1.1rem}
        .gp-addr-main{font-family:var(--serif);font-size:1.05rem;color:var(--text);margin-bottom:0.25rem}
        .gp-addr-hint{font-size:12px;color:var(--text-muted);font-style:italic}
        .gp-map{display:inline-flex;align-items:center;gap:5px;margin-top:0.75rem;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:var(--gold);text-decoration:none;border:1px solid var(--border);border-radius:4px;padding:0.4rem 0.75rem}
        .gp-card{background:var(--dark-3);border:1px solid rgba(255,255,255,0.06);border-radius:7px;padding:0.85rem 1rem;margin-bottom:0.6rem}
        .gp-card-num{font-size:8px;font-weight:500;letter-spacing:0.2em;color:var(--gold);text-transform:uppercase;margin-bottom:0.2rem}
        .gp-card-title{font-family:var(--serif);font-size:0.95rem;color:var(--text);margin-bottom:0.15rem}
        .gp-card-desc{font-size:12px;color:var(--text-muted);line-height:1.5}
        .gp-pw{background:var(--dark-3);border:1px solid var(--border);border-radius:7px;padding:1.1rem 1rem;text-align:center;margin-bottom:1rem}
        .gp-pw-lbl{font-size:8px;letter-spacing:0.25em;text-transform:uppercase;color:var(--text-muted);margin-bottom:0.5rem}
        .gp-pw-code{font-family:'Courier New',monospace;font-size:2.2rem;font-weight:700;color:var(--gold);letter-spacing:0.35em;transition:filter 0.4s;cursor:pointer}
        .gp-pw-hint{font-size:11px;color:var(--text-muted);margin-top:0.4rem;font-style:italic}
        .gp-reveal{margin-top:0.75rem;background:transparent;border:1px solid var(--border);color:var(--gold);font-size:10px;letter-spacing:0.15em;text-transform:uppercase;padding:0.4rem 1.1rem;border-radius:4px;cursor:pointer}
        .gp-vid-lbl{font-size:8px;font-weight:500;letter-spacing:0.2em;text-transform:uppercase;color:var(--gold);margin-bottom:0.4rem;display:flex;align-items:center;gap:5px}
        .gp-vid-lbl::before{content:'▶';font-size:7px}
        .gp-vid{border-radius:7px;overflow:hidden;border:1px solid var(--border);width:100%;aspect-ratio:16/9;background:#000}
        .gp-vid iframe{width:100%;height:100%;display:block;border:none}
        .gp-wifi{display:grid;grid-template-columns:1fr 1fr;gap:0.6rem}
        .gp-wifi-card{background:var(--dark-3);border:1px solid rgba(255,255,255,0.06);border-radius:7px;padding:0.85rem}
        .gp-wifi-icon{width:26px;height:26px;border-radius:50%;background:rgba(201,168,76,0.1);display:flex;align-items:center;justify-content:center;margin-bottom:0.5rem}
        .gp-wifi-sub{font-size:8px;letter-spacing:0.2em;text-transform:uppercase;color:var(--text-muted);margin-bottom:0.2rem}
        .gp-wifi-val{font-family:var(--serif);font-size:0.9rem;color:var(--text)}
        .gp-rules{display:grid;gap:0.75rem}
        .gp-rule{display:flex;align-items:flex-start;gap:10px;font-size:13px;color:var(--text-muted);line-height:1.5}
        .gp-dot{width:18px;height:18px;border-radius:50%;background:rgba(255,255,255,0.04);display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px}
        .gp-dot.ok{background:rgba(201,168,76,0.1)}
        .gp-contact{background:var(--dark-3);border:1px solid var(--border);border-radius:7px;padding:1.1rem;text-align:center}
        .gp-phone{font-family:var(--serif);font-size:1.4rem;color:var(--gold);margin:0.3rem 0 0.4rem}
        .gp-csub{font-size:11px;color:var(--text-muted);margin-bottom:1rem}
        .gp-social{display:grid;grid-template-columns:1fr 1fr;gap:0.5rem}
        .gp-sbtn{display:flex;align-items:center;justify-content:center;gap:6px;padding:0.6rem 0.4rem;border:1px solid var(--border);border-radius:7px;font-size:12px;font-weight:500;color:var(--gold-light);text-decoration:none}
        .gp-sbtn.zalo{background:rgba(0,104,255,0.07);border-color:rgba(0,104,255,0.25);color:#7ab8ff}
        .gp-footer{padding:1.25rem;text-align:center;border-top:1px solid var(--border);position:relative}
        .gp-footer::after{content:'';position:absolute;bottom:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent,var(--gold),transparent)}
        .gp-fbrand{font-family:var(--serif);font-style:italic;font-size:1rem;color:var(--gold);margin-bottom:0.3rem}
        .gp-ftext{font-size:11px;color:var(--text-muted)}
      `}</style>

      <div className="gp">
        {/* Header */}
        <div className="gp-hdr">
          <div className="gp-brand">23st.homestay · Hướng dẫn nhận phòng</div>
          <div className="gp-name">{room.name || "Tên phòng"}</div>
          <div className="gp-sub">{room.district || "Quận/huyện"}, Hồ Chí Minh</div>
          <div className="gp-div" />
        </div>

        {heroImg && <img src={heroImg} alt={room.name} className="gp-hero" />}

        {/* Địa chỉ */}
        <div className="gp-sec">
          <div className="gp-lbl">Địa chỉ</div>
          <div className="gp-addr">
            <div className="gp-addr-main">{room.address || "Địa chỉ phòng"}</div>
            {g.addressHint && <div className="gp-addr-hint">{g.addressHint}</div>}
            {g.parkingInfo && <div className="gp-addr-hint" style={{ marginTop: "0.2rem" }}>🏍️ {g.parkingInfo}</div>}
            {g.googleMapsUrl && (
              <span className="gp-map">
                <svg viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2" width="10" height="10">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                Mở Google Maps
              </span>
            )}
          </div>
        </div>

        {/* Bước 1 */}
        {(g.lockBoxCode || g.lockBoxLocation) && (
          <div className="gp-sec">
            <div className="gp-lbl">Bước 1 · Lấy chìa khoá</div>
            <div className="gp-card">
              {g.lockBoxLocation && <div className="gp-card-num">{g.lockBoxLocation}</div>}
              <div className="gp-card-title">Nhập mật khẩu để lấy chip</div>
              <div className="gp-card-desc">Nhập mã bên dưới để mở hộp khoá, lấy chip và chìa khoá ra nhé.</div>
            </div>
            {g.lockBoxCode && (
              <div className="gp-pw">
                <div className="gp-pw-lbl">Mật khẩu hộp khoá — {room.name}</div>
                <div
                  className="gp-pw-code"
                  style={{ filter: pwRevealed ? "none" : "blur(8px)" }}
                  onClick={() => setPwRevealed(true)}
                >
                  {g.lockBoxCode}
                </div>
                {!pwRevealed && (
                  <>
                    <div className="gp-pw-hint">Nhấn để hiện mật khẩu</div>
                    <button className="gp-reveal" onClick={() => setPwRevealed(true)}>Hiện mật khẩu</button>
                  </>
                )}
              </div>
            )}
            {g.lockBoxVideoUrl && (
              <>
                <div className="gp-vid-lbl">Clip hướng dẫn dùng chip mở cửa</div>
                <div className="gp-vid">
                  <iframe src={normalizeVideoEmbedUrl(g.lockBoxVideoUrl)} allowFullScreen allow="fullscreen" title="guide-video" />
                </div>
              </>
            )}
          </div>
        )}

        {/* Bước 2 */}
        {g.gateInstructions && (
          <div className="gp-sec">
            <div className="gp-lbl">Bước 2 · Vào trong nhà</div>
            <div className="gp-card">
              <div className="gp-card-num">Cổng chính</div>
              <div className="gp-card-title">Quét chip · vào sân</div>
              <div className="gp-card-desc">{g.gateInstructions}</div>
            </div>
            {g.buildingInstructions && (
              <div className="gp-card">
                <div className="gp-card-num">Trong sân</div>
                <div className="gp-card-title">Lên cầu thang</div>
                <div className="gp-card-desc">{g.buildingInstructions}</div>
              </div>
            )}
          </div>
        )}

        {/* Bước 3 */}
        {g.roomNumber && (
          <div className="gp-sec">
            <div className="gp-lbl">Bước 3 · Vào phòng</div>
            <div className="gp-card">
              <div className="gp-card-num">Phòng số {g.roomNumber}</div>
              <div className="gp-card-title">Dùng chìa khoá mở cửa phòng</div>
              <div className="gp-card-desc">
                Tìm biển số <strong style={{ color: "var(--gold)" }}>{g.roomNumber}</strong> trên cửa. Dùng chìa khoá vừa lấy từ hộp để mở — welcome home! 🏡
              </div>
            </div>
          </div>
        )}

        {/* WiFi */}
        {(g.wifiName || g.wifiPassword) && (
          <div className="gp-sec">
            <div className="gp-lbl">Tiện ích</div>
            <div className="gp-wifi">
              {g.wifiName && (
                <div className="gp-wifi-card">
                  <div className="gp-wifi-icon">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2">
                      <path d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01"/>
                    </svg>
                  </div>
                  <div className="gp-wifi-sub">Tên WiFi</div>
                  <div className="gp-wifi-val">{g.wifiName}</div>
                </div>
              )}
              {g.wifiPassword && (
                <div className="gp-wifi-card">
                  <div className="gp-wifi-icon">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </div>
                  <div className="gp-wifi-sub">Mật khẩu WiFi</div>
                  <div className="gp-wifi-val">{g.wifiPassword}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Nội quy */}
        <div className="gp-sec">
          <div className="gp-lbl">Nội quy</div>
          <div className="gp-rules">
            {DEFAULT_RULES.map((rule, i) => (
              <div className="gp-rule" key={i}>
                <div className={`gp-dot${rule.allowed ? " ok" : ""}`}>
                  {rule.allowed
                    ? <svg viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.5" width="9" height="9"><polyline points="20 6 9 17 4 12"/></svg>
                    : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="9" height="9"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  }
                </div>
                {rule.text}
              </div>
            ))}
          </div>
        </div>

        {/* Hỗ trợ */}
        <div className="gp-sec">
          <div className="gp-lbl">Hỗ trợ</div>
          <div className="gp-contact">
            <div style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>Hotline 23ST Homestay</div>
            <div className="gp-phone">078 868 2323</div>
            <div className="gp-csub">Hỗ trợ 07:00 – 24:00 mỗi ngày</div>
            <div className="gp-social">
              <span className="gp-sbtn">
                <svg viewBox="0 0 24 24" fill="#4267B2" width="14" height="14"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
                Facebook
              </span>
              <span className="gp-sbtn">
                <svg viewBox="0 0 24 24" width="14" height="14">
                  <defs><linearGradient id="ig2" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#f09433"/><stop offset="100%" stopColor="#bc1888"/></linearGradient></defs>
                  <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#ig2)"/>
                  <circle cx="12" cy="12" r="4" fill="none" stroke="white" strokeWidth="2"/>
                  <circle cx="17.5" cy="6.5" r="1" fill="white"/>
                </svg>
                Instagram
              </span>
              <span className="gp-sbtn">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="var(--text)"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/></svg>
                TikTok
              </span>
              <span className="gp-sbtn zalo">
                <svg viewBox="0 0 32 32" width="14" height="14" fill="#4a9eff"><path d="M16 2C8.268 2 2 8.268 2 16s6.268 14 14 14 14-6.268 14-14S23.732 2 16 2zm-3.5 19.5h-2v-9h2v9zm5.5 0h-2v-5.5c0-.828-.672-1.5-1.5-1.5s-1.5.672-1.5 1.5v5.5h-2v-9h2v1.1a3.494 3.494 0 0 1 2.5-1.1c1.93 0 3.5 1.57 3.5 3.5v5.5zm3.5 0h-2v-9h2v9z"/></svg>
                Zalo
              </span>
            </div>
          </div>
        </div>

        <div className="gp-footer">
          <div className="gp-fbrand">23st.Homestay</div>
          <div className="gp-ftext">Cảm ơn bạn đã tin tưởng · Chúc bạn kỳ lưu trú thật vui!</div>
        </div>
      </div>
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function GuideManagePage(): React.ReactElement {
  const { user } = useAuth();
  const canEdit = user?.role === "superadmin" || user?.role === "admin";

  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Room | null>(null);
  const [guide, setGuide] = useState<CheckInGuide>(EMPTY_GUIDE);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [formLang, setFormLang] = useState<"vi" | "en">("vi");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const filteredRooms = useMemo(() =>
    !search ? rooms : rooms.filter(r =>
      r.name.toLowerCase().includes(search.toLowerCase()) ||
      r.slug.toLowerCase().includes(search.toLowerCase())
    ),
    [rooms, search]
  );

  useEffect(() => { setPage(1); }, [search]);

  const totalPages = Math.ceil(filteredRooms.length / PAGE_SIZE);
  const pageRooms = useMemo(
    () => filteredRooms.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredRooms, page]
  );

  const handleCopyLink = (slug: string, id: string) => {
    navigator.clipboard.writeText(`${PUBLIC_URL}/guide/${slug}`).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const fetchRooms = useCallback(async () => {
    try {
      const res = await roomsAPI.getAll();
      let all: Room[] = res.data.data || [];
      if (user?.role === "roommaster" && user?.managedRooms) {
        const ids = (user.managedRooms as any[]).map((r) => (typeof r === "object" ? r._id : r));
        all = all.filter((r) => ids.includes(r._id));
      }
      setRooms(all);
    } catch {
      toast.error("Lỗi khi tải danh sách phòng");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const handleOpen = (room: Room) => {
    setEditing(room);
    setGuide({
      lockBoxCode:              room.checkInGuide?.lockBoxCode              || "",
      lockBoxLocation:          room.checkInGuide?.lockBoxLocation          || "",
      lockBoxLocationEn:        room.checkInGuide?.lockBoxLocationEn        || "",
      lockBoxVideoUrl:          room.checkInGuide?.lockBoxVideoUrl          || "",
      roomNumber:               room.checkInGuide?.roomNumber               || "",
      gateInstructions:         room.checkInGuide?.gateInstructions         || "",
      gateInstructionsEn:       room.checkInGuide?.gateInstructionsEn       || "",
      buildingInstructions:     room.checkInGuide?.buildingInstructions     || "",
      buildingInstructionsEn:   room.checkInGuide?.buildingInstructionsEn   || "",
      wifiName:                 room.checkInGuide?.wifiName                 || "",
      wifiPassword:             room.checkInGuide?.wifiPassword             || "",
      parkingInfo:              room.checkInGuide?.parkingInfo              || "",
      parkingInfoEn:            room.checkInGuide?.parkingInfoEn            || "",
      googleMapsUrl:            room.checkInGuide?.googleMapsUrl            || "",
      addressHint:              room.checkInGuide?.addressHint              || "",
      addressHintEn:            room.checkInGuide?.addressHintEn            || "",
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await roomsAPI.update(editing._id, { checkInGuide: guide } as any);
      toast.success("Đã lưu hướng dẫn");
      setEditing(null);
      fetchRooms();
    } catch {
      toast.error("Lỗi khi lưu");
    } finally {
      setSaving(false);
    }
  };

  const set = (key: keyof CheckInGuide) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setGuide((g) => ({ ...g, [key]: e.target.value }));

  const inputCls = "w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white";
  const labelCls = "block text-xs font-medium text-zinc-600 mb-1";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" />
      </div>
    );
  }

  // ── Split-panel editor ──────────────────────────────────────────────────────
  if (editing) {
    return (
      <div className="flex -m-5 lg:-m-6" style={{ height: "calc(100vh - 56px)" }}>

        {/* Left: form */}
        <div className="w-[400px] xl:w-[440px] flex-shrink-0 flex flex-col border-r border-zinc-200 bg-white">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-100 bg-zinc-50">
            <div>
              <div className="font-semibold text-zinc-900 text-sm">{editing.name}</div>
              <a
                href={`${PUBLIC_URL}/guide/${editing.slug}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-amber-600 hover:underline"
              >
                /guide/{editing.slug} ↗
              </a>
            </div>
            <button
              onClick={() => setEditing(null)}
              className="text-zinc-400 hover:text-zinc-700 p-1.5 rounded-md hover:bg-zinc-100 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          {/* Fields */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3.5">

            {/* Lang tab */}
            <div className="flex gap-1.5 p-1 bg-zinc-100 rounded-lg -mt-1">
              <button
                onClick={() => setFormLang("vi")}
                className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-colors ${formLang === "vi" ? "bg-white text-zinc-800 shadow-sm" : "text-zinc-400 hover:text-zinc-600"}`}
              >
                🇻🇳 Tiếng Việt
              </button>
              <button
                onClick={() => setFormLang("en")}
                className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-colors ${formLang === "en" ? "bg-white text-zinc-800 shadow-sm" : "text-zinc-400 hover:text-zinc-600"}`}
              >
                🇬🇧 English
              </button>
            </div>

            {/* Universal fields — always shown */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Mã hộp khóa</label>
                <input className={inputCls} value={guide.lockBoxCode || ""} onChange={set("lockBoxCode")} placeholder="8602" />
              </div>
              <div>
                <label className={labelCls}>Số phòng</label>
                <input className={inputCls} value={guide.roomNumber || ""} onChange={set("roomNumber")} placeholder="203" />
              </div>
              <div>
                <label className={labelCls}>Tên WiFi</label>
                <input className={inputCls} value={guide.wifiName || ""} onChange={set("wifiName")} placeholder="HND house 5g" />
              </div>
              <div>
                <label className={labelCls}>Mật khẩu WiFi</label>
                <input className={inputCls} value={guide.wifiPassword || ""} onChange={set("wifiPassword")} placeholder="68686868" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Link Google Maps</label>
              <input type="url" className={inputCls} value={guide.googleMapsUrl || ""} onChange={set("googleMapsUrl")} placeholder="https://maps.google.com/..." />
            </div>
            <div>
              <label className={labelCls}>Video hướng dẫn mở cửa (dán link Google Drive hoặc YouTube)</label>
              <input type="url" className={inputCls} value={guide.lockBoxVideoUrl || ""} onChange={set("lockBoxVideoUrl")} placeholder="https://drive.google.com/file/d/.../view?usp=sharing" />
              <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
                Dán link chia sẻ bình thường là được — hệ thống tự chuyển sang dạng nhúng được.{" "}
                <strong className="text-zinc-500">Với Google Drive:</strong> nhớ mở quyền chia sẻ file thành{" "}
                <strong className="text-zinc-500">"Bất kỳ ai có đường liên kết"</strong> (Anyone with the link),
                nếu không khách sẽ thấy màn hình xin quyền truy cập thay vì video.
              </p>
            </div>

            {/* Language-specific fields */}
            {formLang === "vi" ? (
              <>
                <div>
                  <label className={labelCls}>Vị trí hộp khóa</label>
                  <input className={inputCls} value={guide.lockBoxLocation || ""} onChange={set("lockBoxLocation")} placeholder="Hộp xám — bên phải cổng" />
                </div>
                <div>
                  <label className={labelCls}>Thông tin đỗ xe</label>
                  <input className={inputCls} value={guide.parkingInfo || ""} onChange={set("parkingInfo")} placeholder="Tối đa 1 xe máy (miễn phí)" />
                </div>
                <div>
                  <label className={labelCls}>Gợi ý tìm đường</label>
                  <input className={inputCls} value={guide.addressHint || ""} onChange={set("addressHint")} placeholder='Google Maps → tìm "..." → đi đến cuối hẻm' />
                </div>
                <div>
                  <label className={labelCls}>Hướng dẫn vào cổng</label>
                  <textarea rows={2} className={inputCls + " resize-y"} value={guide.gateInstructions || ""} onChange={set("gateInstructions")}
                    placeholder="Chạm chip vào máy quét ở cổng. Dắt xe vào, đi tới cuối hành lang..." />
                </div>
                <div>
                  <label className={labelCls}>Hướng dẫn cầu thang / vào phòng</label>
                  <textarea rows={2} className={inputCls + " resize-y"} value={guide.buildingInstructions || ""} onChange={set("buildingInstructions")}
                    placeholder="Cầu thang sắt ngay sau khi quẹo trái. Lên lầu, ra hành lang ngoài trời..." />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className={labelCls}>Lockbox location</label>
                  <input className={inputCls} value={guide.lockBoxLocationEn || ""} onChange={set("lockBoxLocationEn")} placeholder="Gray box — right side of the gate" />
                </div>
                <div>
                  <label className={labelCls}>Parking info</label>
                  <input className={inputCls} value={guide.parkingInfoEn || ""} onChange={set("parkingInfoEn")} placeholder="1 motorbike (free)" />
                </div>
                <div>
                  <label className={labelCls}>Directions hint</label>
                  <input className={inputCls} value={guide.addressHintEn || ""} onChange={set("addressHintEn")} placeholder='Google Maps → search "..." → end of the alley' />
                </div>
                <div>
                  <label className={labelCls}>Gate instructions</label>
                  <textarea rows={2} className={inputCls + " resize-y"} value={guide.gateInstructionsEn || ""} onChange={set("gateInstructionsEn")}
                    placeholder="Tap the chip on the scanner at the gate. Walk to the end of the corridor..." />
                </div>
                <div>
                  <label className={labelCls}>Staircase / room instructions</label>
                  <textarea rows={2} className={inputCls + " resize-y"} value={guide.buildingInstructionsEn || ""} onChange={set("buildingInstructionsEn")}
                    placeholder="Iron staircase on your left. Go upstairs, walk to the outdoor corridor..." />
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex gap-2.5 px-5 py-3.5 border-t border-zinc-100 bg-zinc-50">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-semibold py-2 rounded-lg text-sm transition-colors"
            >
              {saving ? "Đang lưu…" : "Lưu hướng dẫn"}
            </button>
            <button
              onClick={() => setEditing(null)}
              className="flex-1 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-semibold py-2 rounded-lg text-sm transition-colors"
            >
              Hủy
            </button>
          </div>
        </div>

        {/* Right: live preview */}
        <div className="flex-1 overflow-y-auto bg-zinc-200" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #c4c4c4 1px, transparent 0)", backgroundSize: "24px 24px" }}>
          <div className="flex flex-col items-center py-8 px-6">
            <div className="text-xs text-zinc-500 font-medium mb-4 tracking-wide uppercase">Preview — trang khách</div>
            <div style={{ width: "100%", maxWidth: 390 }}>
              <GuidePreview room={editing} guide={guide} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Room list ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-zinc-900">Hướng dẫn nhận phòng</h1>
        <p className="text-sm text-zinc-500 mt-0.5">
          Cấu hình thông tin nhận phòng cho từng phòng — khách xem tại{" "}
          <a
            href={`${PUBLIC_URL}/guide/[slug]`}
            className="font-mono bg-zinc-100 px-1 rounded text-xs text-amber-700 hover:underline"
          >
            {PUBLIC_URL}/guide/[slug]
          </a>
        </p>
      </div>

      <div className="flex items-center gap-3 bg-white border border-zinc-200 rounded-xl px-4 py-3">
        <input
          type="text"
          placeholder="Tìm tên phòng, slug..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 text-sm focus:outline-none text-zinc-700 placeholder-zinc-400"
        />
        {search && (
          <button onClick={() => setSearch("")} className="text-xs text-zinc-400 hover:text-zinc-600">✕</button>
        )}
        <span className="text-xs text-zinc-400 whitespace-nowrap">{filteredRooms.length} / {rooms.length} phòng</span>
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100 bg-zinc-50">
              <th className="text-left px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide">Phòng</th>
              <th className="text-left px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide hidden sm:table-cell">Số phòng</th>
              <th className="text-left px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide hidden md:table-cell">WiFi</th>
              <th className="text-center px-4 py-3 font-medium text-zinc-500 text-xs uppercase tracking-wide">Trạng thái</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {pageRooms.map((room) => {
              const configured = isGuideConfigured(room.checkInGuide);
              return (
                <tr key={room._id} className="border-b border-zinc-50 hover:bg-zinc-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-800">{room.name}</div>
                    <div className="text-xs text-zinc-400">{room.slug}</div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-zinc-600">
                    {room.checkInGuide?.roomNumber || <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-zinc-600 font-mono text-xs">
                    {room.checkInGuide?.wifiName || <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${configured ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-400"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${configured ? "bg-emerald-500" : "bg-zinc-300"}`} />
                      {configured ? "Đã cấu hình" : "Chưa có"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => handleCopyLink(room.slug, room._id)}
                        title="Copy link hướng dẫn"
                        className={`p-1.5 rounded-md transition-colors ${copiedId === room._id ? "text-emerald-600 bg-emerald-50" : "text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"}`}
                      >
                        {copiedId === room._id
                          ? <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                          : <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                        }
                      </button>
                      <a
                        href={`${PUBLIC_URL}/guide/${room.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Xem trang khách"
                        className="p-1.5 rounded-md text-zinc-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      </a>
                      {canEdit && (
                        <button
                          onClick={() => handleOpen(room)}
                          title="Chỉnh sửa hướng dẫn"
                          className="p-1.5 rounded-md text-zinc-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rooms.length === 0 && (
          <div className="text-center py-16 text-sm text-zinc-400">Chưa có phòng nào</div>
        )}
        <Pagination page={page} totalPages={totalPages} total={filteredRooms.length} pageSize={PAGE_SIZE} onChange={setPage} />
      </div>
    </div>
  );
}
